import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditor, EditorContent } from '@tiptap/react';
import { useTheme } from '@mui/material/styles';
import { Box, CircularProgress, Typography } from '@mui/material';

// TipTap extensions
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import CharacterCount from '@tiptap/extension-character-count';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Highlight from '@tiptap/extension-highlight';
import TextStyle from '@tiptap/extension-text-style';

// Custom extensions
import { TabKeyHandler } from './extensions/TabKeyHandler';
import { RichTable } from './extensions/CustomTable';
import { ResizableImage } from './extensions/CustomImage';
import { TextColor } from './extensions/CustomColor';
import { TypographyExtension } from './extensions/CustomFont';
import { PageBreakExtension } from './extensions/PageBreakExtension';

// Components
import EditorNotification from './components/EditorNotification';
import ToolbarPlugin from './plugins/ToolbarPlugin';

import './Editor.css';

import { backendClient } from '../../../api/backend';

const TemplateEditor = ({ template, templateId }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [editorReady, setEditorReady] = useState(false);
  const [selectedImageNode, setSelectedImageNode] = useState(null);

  // Save state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const lastSavedContent = useRef('');
  const autosaveTimeoutRef = useRef(null);
  const hasUnsavedRef = useRef(false);
  hasUnsavedRef.current = hasUnsavedChanges;
  const savingRef = useRef(false);

  const closeNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  // ── TipTap editor ─────────────────────────────────────────────────────────
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        history: true,
        bulletList: { HTMLAttributes: { class: 'bullet-list' } },
        orderedList: { HTMLAttributes: { class: 'ordered-list' } },
        listItem: { HTMLAttributes: { class: 'list-item' } },
      }),
      Placeholder.configure({ placeholder: t('template_editor.placeholder', 'Start writing your template...') }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph', 'orderedList', 'bulletList'],
        alignments: ['left', 'center', 'right', 'justify'],
      }),
      Highlight,
      TaskList,
      TaskItem,
      CharacterCount.configure({ limit: 100000 }),
      TextStyle,
      TabKeyHandler,
      RichTable,
      TextColor,
      TypographyExtension,
      ResizableImage,
      PageBreakExtension,
    ],
    onCreate: () => setEditorReady(true),
    content: template?.content || '<p></p>',
    editable: true,
    editorProps: { attributes: { class: 'focus:outline-none' } },
  });

  // Store initial content once editor is ready
  useEffect(() => {
    if (editor && editorReady) {
      lastSavedContent.current = editor.getHTML();
    }
  }, [editor, editorReady]);

  // ── Save function ─────────────────────────────────────────────────────────
  const saveTemplate = useCallback(async () => {
    if (!editor || !hasUnsavedRef.current || savingRef.current) return;

    savingRef.current = true;
    setNotification({ open: true, message: t('document_editor.saving_document'), severity: 'info' });

    try {
      const content = editor.getHTML();
      await backendClient.updateTemplate(templateId, { content });
      lastSavedContent.current = content;
      setHasUnsavedChanges(false);
      setTimeout(() => {
        setNotification({ open: true, message: t('document_editor.document_saved'), severity: 'success' });
      }, 500);
    } catch (err) {
      console.error('Failed to save template:', err);
      setNotification({ open: true, message: t('document_editor.cannot_save'), severity: 'error' });
    } finally {
      savingRef.current = false;
    }
  }, [editor, templateId, t]);

  // ── Detect changes + autosave (20s) ────────────────────────────────────────
  useEffect(() => {
    if (!editor) return;

    const handleDocumentChange = () => {
      const currentContent = editor.getHTML();
      if (currentContent !== lastSavedContent.current) {
        setHasUnsavedChanges(true);
      } else {
        setHasUnsavedChanges(false);
        return;
      }

      clearTimeout(autosaveTimeoutRef.current);
      autosaveTimeoutRef.current = setTimeout(() => {
        if (hasUnsavedRef.current) saveTemplate();
      }, 20000);
    };

    editor.on('update', handleDocumentChange);
    return () => {
      editor.off('update', handleDocumentChange);
      clearTimeout(autosaveTimeoutRef.current);
    };
  }, [editor, saveTemplate]);

  // ── Save on tab hide ──────────────────────────────────────────────────────
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && hasUnsavedRef.current) {
        saveTemplate();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [saveTemplate]);

  // ── Cmd/Ctrl+S ────────────────────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        saveTemplate();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [saveTemplate]);

  // ── Save on unmount ───────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      clearTimeout(autosaveTimeoutRef.current);
      if (hasUnsavedRef.current && editor) {
        const content = editor.getHTML();
        backendClient.updateTemplate(templateId, { content }).catch(() => {});
      }
    };
  }, [editor, templateId]);

  // ── Track selected image ──────────────────────────────────────────────────
  useEffect(() => {
    if (!editor) return;
    const handleSelectionUpdate = () => {
      if (!editor.isActive('resizableImage')) {
        setSelectedImageNode(null);
        return;
      }
      const { state } = editor;
      const { from } = state.selection;
      let pos = from;
      state.doc.nodesBetween(from, from, (node, nodePos) => {
        if (node.type.name === 'resizableImage') { pos = nodePos; return false; }
        return true;
      });
      setSelectedImageNode({ node: state.doc.nodeAt(pos), pos });
    };
    editor.on('selectionUpdate', handleSelectionUpdate);
    editor.on('focus', handleSelectionUpdate);
    return () => {
      editor.off('selectionUpdate', handleSelectionUpdate);
      editor.off('focus', handleSelectionUpdate);
    };
  }, [editor]);

  // ── Render ────────────────────────────────────────────────────────────────

  const isDarkMode = theme.palette.mode === 'dark';
  const templateName = template?.name
    ? (typeof template.name === 'string' ? template.name : Object.values(template.name)[0] || 'Untitled')
    : 'Untitled';

  return (
    <Box className={`editor-container${isDarkMode ? ' dark-mode' : ''}`}>
      {/* Toolbar */}
      <Box
        className="toolbar"
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexDirection: 'column',
          position: 'sticky',
          top: 0,
          zIndex: 1300,
        }}
      >
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
          {editor && (
            <ToolbarPlugin
              editor={editor}
              socketRef={{ current: null }}
              contractTitle={templateName}
              saveContract={saveTemplate}
              selectedImageNode={selectedImageNode}
              hasUnsavedChanges={hasUnsavedChanges}
              aiButtonRef={{ current: null }}
              contractId={null}
              pageSettings={null}
              onPageSettingsChange={null}
              suggestModeActive={false}
              suggestModeLocked={false}
              onSuggestModeChange={() => {}}
              mode="template"
            />
          )}
        </Box>
      </Box>

      {/* Editor body */}
      <Box className="editor-body">
        <Box className="editor-content-column editor-scroll-container">
          {!editor || !editorReady ? (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '85vh',
              backgroundColor: theme.palette.background.paper,
            }}>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              <Typography>{t('document_editor.loading_content')}</Typography>
            </Box>
          ) : (
            <EditorContent
              editor={editor}
              className="tiptap-editor"
            />
          )}
        </Box>
      </Box>

      {/* Save notification */}
      <EditorNotification notification={notification} onClose={closeNotification} />
    </Box>
  );
};

export default TemplateEditor;
